/**
 * useRealtimeSync — subscribe to Supabase Realtime for cross-device sync.
 *
 * Listens for INSERT/UPDATE events on the four data tables (training_sessions,
 * meal_logs, training_feedback, children) filtered to the current user.
 *
 * On receiving a remote change:
 *  1. Writes the updated data into localStorage (same format as loadAllUserData).
 *  2. Dispatches `energymatch:remote-update` so screens can re-read their data.
 *
 * No-op when Supabase is not configured or user is not authenticated.
 */

import { useEffect } from 'react'
import { getSupabase } from '../lib/supabaseClient'
import type {
  ChildProfile,
  TrainingSession,
  DailyMealLog,
  ExtraSnackLog,
  HydrationLogEntry,
  DailyTrainingFeedback,
} from '../types'

export interface RemoteUpdateEvent {
  table: 'training_sessions' | 'meal_logs' | 'training_feedback' | 'children'
  childId?: string
  dateKey?: string
}

function dispatch(detail: RemoteUpdateEvent): void {
  window.dispatchEvent(
    new CustomEvent<RemoteUpdateEvent>('energymatch:remote-update', { detail })
  )
}

export function useRealtimeSync(userId: string | null): void {
  useEffect(() => {
    const supabase = getSupabase()
    if (!supabase || !userId) return

    const channel = supabase
      .channel(`user-sync-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'training_sessions', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return
          const row = payload.new as { child_id: string; date_key: string; data: TrainingSession[] }
          const { child_id: childId, date_key: dateKey, data } = row
          try {
            const raw = localStorage.getItem(`energymatch-sessions-v2-${childId}`)
            const existing: Record<string, TrainingSession[]> = raw ? JSON.parse(raw) : {}
            existing[dateKey] = data
            localStorage.setItem(`energymatch-sessions-v2-${childId}`, JSON.stringify(existing))
          } catch { /* quota */ }
          dispatch({ table: 'training_sessions', childId, dateKey })
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'meal_logs', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return
          const row = payload.new as {
            child_id: string
            date_key: string
            slots: DailyMealLog
            extra_snacks: ExtraSnackLog[]
            hydration: HydrationLogEntry[]
          }
          const { child_id: childId, date_key: dateKey, slots, extra_snacks, hydration } = row
          try {
            const raw = localStorage.getItem(`energymatch-daily-logs-${childId}`)
            const existing: Record<string, DailyMealLog> = raw ? JSON.parse(raw) : {}
            existing[dateKey] = slots
            localStorage.setItem(`energymatch-daily-logs-${childId}`, JSON.stringify(existing))
          } catch { /* quota */ }
          try {
            const raw = localStorage.getItem(`energymatch-extra-snacks-${childId}`)
            const existing: Record<string, ExtraSnackLog[]> = raw ? JSON.parse(raw) : {}
            existing[dateKey] = extra_snacks
            localStorage.setItem(`energymatch-extra-snacks-${childId}`, JSON.stringify(existing))
          } catch { /* quota */ }
          try {
            const raw = localStorage.getItem(`energymatch-hydration-${childId}`)
            const existing: Record<string, HydrationLogEntry[]> = raw ? JSON.parse(raw) : {}
            existing[dateKey] = hydration
            localStorage.setItem(`energymatch-hydration-${childId}`, JSON.stringify(existing))
          } catch { /* quota */ }
          dispatch({ table: 'meal_logs', childId, dateKey })
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'training_feedback', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return
          const row = payload.new as { child_id: string; date_key: string; data: DailyTrainingFeedback }
          const { child_id: childId, date_key: dateKey, data } = row
          try {
            const raw = localStorage.getItem(`energymatch-training-feedback-${childId}`)
            const existing: Record<string, DailyTrainingFeedback> = raw ? JSON.parse(raw) : {}
            existing[dateKey] = data
            localStorage.setItem(`energymatch-training-feedback-${childId}`, JSON.stringify(existing))
          } catch { /* quota */ }
          dispatch({ table: 'training_feedback', childId, dateKey })
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'children', filter: `user_id=eq.${userId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') return
          const row = payload.new as { profile_data: ChildProfile }
          const incoming = row.profile_data
          try {
            const raw = localStorage.getItem('energymatch-children')
            const existing: ChildProfile[] = raw ? JSON.parse(raw) : []
            const idx = existing.findIndex((c) => c.id === incoming.id)
            if (idx >= 0) {
              existing[idx] = incoming
            } else {
              existing.push(incoming)
            }
            localStorage.setItem('energymatch-children', JSON.stringify(existing))
          } catch { /* quota */ }
          dispatch({ table: 'children' })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId])
}
